import { useCallback, useEffect, useRef, useState } from 'react';
import { compositionFluency } from './baseline';
import { wpm as wpmFn } from './stats';
import {
  isPrintableTypingKey,
  shouldIgnoreKeyEvent,
} from './typingKey';

export const FREE_WRITE_DURATION_MS = 60_000;

export interface FreeWriteResult {
  text: string;
  wpm: number;
  accuracy: number;
  correctChars: number;
  totalKeystrokes: number;
  backspaces: number;
  durationMs: number;
}

export interface UseFreeWriteInputState {
  text: string;
  elapsedMs: number;
  remainingMs: number;
  liveWpm: number;
  liveFluency: number;
  finished: boolean;
  finish: () => void;
}

export function useFreeWriteInput(
  durationMs: number = FREE_WRITE_DURATION_MS,
  onFinish: (result: FreeWriteResult) => void,
): UseFreeWriteInputState {
  const onFinishRef = useRef(onFinish);
  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  const [text, setText] = useState('');
  const [tick, setTick] = useState(0);
  const stateRef = useRef({
    text: '',
    startedAtMs: 0,
    correctChars: 0,
    totalKeystrokes: 0,
    backspaces: 0,
    finished: false,
  });

  const forceRerender = useCallback(() => setTick((t) => t + 1), []);

  const finish = useCallback(() => {
    const s = stateRef.current;
    if (s.finished) return;
    s.finished = true;
    const endedAt = Date.now();
    const duration =
      s.startedAtMs === 0 ? 0 : Math.min(durationMs, endedAt - s.startedAtMs);
    const result: FreeWriteResult = {
      text: s.text,
      wpm: wpmFn(s.correctChars, duration),
      accuracy: compositionFluency(s.totalKeystrokes, s.backspaces),
      correctChars: s.correctChars,
      totalKeystrokes: s.totalKeystrokes,
      backspaces: s.backspaces,
      durationMs: duration,
    };
    onFinishRef.current(result);
  }, [durationMs]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const s = stateRef.current;
      if (s.finished) return;
      if (shouldIgnoreKeyEvent(e)) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        if (s.text.length === 0) return;
        s.backspaces += 1;
        s.totalKeystrokes += 1;
        s.text = s.text.slice(0, -1);
        s.correctChars = Math.max(0, s.correctChars - 1);
        setText(s.text);
        forceRerender();
        return;
      }

      if (e.key === 'Dead') return;

      if (!isPrintableTypingKey(e.key)) return;
      e.preventDefault();

      if (s.startedAtMs === 0) s.startedAtMs = Date.now();

      s.totalKeystrokes += 1;
      s.correctChars += 1;
      s.text += e.key;
      setText(s.text);
      forceRerender();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [finish, forceRerender]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const s = stateRef.current;
      if (s.finished || s.startedAtMs === 0) return;
      const elapsed = Date.now() - s.startedAtMs;
      if (elapsed >= durationMs) {
        finish();
        return;
      }
      forceRerender();
    }, 100);
    return () => window.clearInterval(id);
  }, [durationMs, finish, forceRerender]);

  void tick;
  const s = stateRef.current;
  const elapsedMs =
    s.startedAtMs === 0 ? 0 : Math.min(durationMs, Date.now() - s.startedAtMs);
  const remainingMs = Math.max(0, durationMs - elapsedMs);

  return {
    text,
    elapsedMs,
    remainingMs,
    liveWpm: wpmFn(s.correctChars, elapsedMs || 1),
    liveFluency: compositionFluency(s.totalKeystrokes, s.backspaces),
    finished: s.finished,
    finish,
  };
}
